/**
 * Compact automation cell: switch + title + description (Typography atom).
 * Capability (`hasAutomationRule`) comes from the backend; ON/OFF is
 * `automationEnabled` on the stage row.
 */

import { useTranslation } from 'react-i18next';
import { Typography } from '@/components/ui/atoms/Typography';
import { StageAutomationSwitch } from './StageAutomationSwitch';

export interface StageAutomationCellProps {
  stageType: string;
  hasAutomationRule: boolean;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
}

function automationDescriptionKey(
  stageType: string,
  configured: boolean,
  enabled: boolean
) {
  // Reached automatically on AI test completion; not an AutomationRule.
  if (stageType === 'interviewed') {
    return 'stage_management.automation_interviewed_desc';
  }
  if (!configured) return 'stage_management.automation_not_configured';
  if (stageType === 'interviewing') {
    return enabled
      ? 'stage_management.automation_interviewing_desc'
      : 'stage_management.automation_interviewing_desc_off';
  }
  if (stageType === 'accepted') {
    return enabled
      ? 'stage_management.automation_accepted_desc'
      : 'stage_management.automation_accepted_desc_off';
  }
  if (stageType === 'rejected') {
    return enabled
      ? 'stage_management.automation_rejected_desc'
      : 'stage_management.automation_rejected_desc_off';
  }
  return null;
}

/** What the stage does, independent of the switch state. */
function automationTitleKey(stageType: string, configured: boolean) {
  if (stageType === 'interviewed') return 'stage_management.automation_title_interviewed';
  if (!configured) return 'stage_management.automation_title_manual';
  if (stageType === 'interviewing') return 'stage_management.automation_title_interviewing';
  if (stageType === 'accepted') return 'stage_management.automation_title_accepted';
  if (stageType === 'rejected') return 'stage_management.automation_title_rejected';
  return null;
}

export function StageAutomationCell({
  stageType,
  hasAutomationRule,
  enabled,
  onEnabledChange,
}: StageAutomationCellProps) {
  const { t } = useTranslation('job');
  const configured = hasAutomationRule;
  const isActive = configured && enabled;
  const descriptionKey = automationDescriptionKey(stageType, configured, enabled);
  const description = descriptionKey ? t(descriptionKey) : '';
  const titleKey = automationTitleKey(stageType, configured);
  const title = titleKey ? t(titleKey) : '';

  return (
    <div className="flex min-w-0 items-center gap-3">
      {configured ? (
        <StageAutomationSwitch
          checked={isActive}
          onChange={onEnabledChange}
          label={title ? `${title}. ${description}` : description}
        />
      ) : (
        // Keeps descriptions aligned with rows that have a switch.
        <span aria-hidden="true" className="w-9 shrink-0" />
      )}
      <div className="min-w-0">
        {title && (
          <Typography
            as="p"
            variant="body-sm"
            weight="semibold"
            color="primary"
            className="leading-snug"
          >
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            as="p"
            variant="body-xs"
            color="muted"
            className="mt-0.5 leading-relaxed"
          >
            {description}
          </Typography>
        )}
      </div>
    </div>
  );
}
